import { Box, Flex, Heading, Text } from '@chakra-ui/react';
import { ReactNode, KeyboardEvent } from 'react';

interface CategoryCardProps {
  title: ReactNode;
  count: number;
  icon?: ReactNode;
  isSelected: boolean;
  onClick: () => void;
}

export const CategoryCard = ({ title, count, icon, isSelected, onClick }: CategoryCardProps) => {
  return (
    <Box
      as='button'
      bg={isSelected ? 'green.G500' : { base: 'white', md: 'white' }}
      color={isSelected ? 'white' : 'dark.D900'}
      p={{ base: '10px', md: 6 }}
      borderRadius={{ base: '50%', md: '16px' }}
      borderWidth='1px'
      borderColor={isSelected ? 'green.G500' : { base: 'dark.D150', md: 'dark.D100' }}
      boxShadow={isSelected ? { base: 'sm', md: 'sm' } : { base: 'none', md: 'sm' }}
      _hover={{
        borderColor: 'green.G500',
        bg: {
          base: isSelected ? 'green.G500' : 'green.G0',
          md: isSelected ? 'green.G500' : 'white',
        },
        transform: { base: 'scale(1.05)', md: 'translateY(-2px)' },
        boxShadow: { base: 'sm', md: 'md' },
      }}
      _active={{
        bg: { base: isSelected ? 'green.G700' : 'green.G50', md: 'white' },
        transform: { base: 'scale(0.95)', md: 'translateY(-2px)' },
      }}
      transition='all 0.2s ease'
      cursor='pointer'
      onClick={onClick}
      tabIndex={0}
      aria-label={`Category with ${count} ${count === 1 ? 'فعالية' : 'فعاليات'}`}
      onKeyDown={(e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      width={{ base: '56px', md: '100%' }}
      height={{ base: '56px', md: 'auto' }}
      minH={{ base: '56px', md: '120px' }}
      flexShrink={0}
      position='relative'
      display='flex'
      alignItems='center'
      justifyContent='center'
    >
      {/* Mobile: Icon Only */}
      <Box
        fontSize={{ base: '24px', md: '32px' }}
        flexShrink={0}
        opacity={isSelected ? 1 : 0.8}
        transition='opacity 0.2s ease'
        display={{ base: 'flex', md: 'none' }}
        alignItems='center'
        justifyContent='center'
      >
        {icon || (
          <Box as='span' fontSize='20px' fontWeight='600' lineHeight='1'>
            •
          </Box>
        )}
      </Box>

      {/* Desktop: Full Card Content */}
      <Flex
        direction='column'
        align='center'
        justify='center'
        gap={2}
        height='100%'
        display={{ base: 'none', md: 'flex' }}
      >
        <Box
          fontSize='32px'
          flexShrink={0}
          opacity={isSelected ? 1 : 0.8}
          transition='opacity 0.2s ease'
        >
          {icon || (
            <Box as='span' fontSize='24px' fontWeight='600'>
              •
            </Box>
          )}
        </Box>
        <Heading
          as='h3'
          variant='headingMedium'
          fontSize='xl'
          fontWeight='600'
          lineHeight='1.2'
          textAlign='center'
        >
          {title}
        </Heading>
        <Text
          textStyle='bodySmallRegular'
          color={isSelected ? 'white' : 'dark.D500'}
          fontSize='sm'
          mt={1}
          dir='rtl'
        >
          {count} {count === 1 ? 'فعالية' : 'فعاليات'}
        </Text>
      </Flex>
    </Box>
  );
};
